import React, { createContext, useState, useEffect, useContext } from 'react';
import { detectLanguage, saveLanguagePreference, getTranslations, isLanguageEnabled } from './i18n';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectLanguage());
  const [translations, setTranslations] = useState(getTranslations(language));

  useEffect(() => {
    // Update translations when language changes
    setTranslations(getTranslations(language));

    // Save preference
    saveLanguagePreference(language);

    // Update HTML lang attribute for accessibility and SEO
    document.documentElement.lang = language;

    // Update text direction for Arabic
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const changeLanguage = (newLang) => {
    if (!isLanguageEnabled(newLang)) return;
    setLanguage(newLang);
  };

  // Translation helper function
  const t = (keyPath, replacements = {}) => {
    const keys = keyPath.split('.');
    let value = translations;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return keyPath; // Return key if translation not found
      }
    }

    // Replace placeholders like {name}
    if (typeof value === 'string' && Object.keys(replacements).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, key) => {
        return replacements[key] !== undefined ? replacements[key] : match;
      });
    }

    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, translations, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
